import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const COUNT = 8000;

function sampleKey() {
  const pts = new Float32Array(COUNT * 3);
  const cos = Math.cos(-0.32), sin = Math.sin(-0.32);
  for (let i = 0; i < COUNT; i++) {
    const r = Math.random();
    let x, y;
    if (r < 0.34) {
      const a = Math.random() * Math.PI * 2;
      const rad = 0.42 + Math.random() * 0.2;
      x = -1.35 + Math.cos(a) * rad;
      y = 0.1 + Math.sin(a) * rad;
    } else if (r < 0.68) {
      x = -0.85 + Math.random() * 2.35;
      y = 0.1 + (Math.random() - 0.5) * 0.17;
    } else {
      const tooth = Math.floor(Math.random() * 3);
      const tx = [0.65, 1.02, 1.36][tooth];
      const tw = [0.16, 0.16, 0.14][tooth];
      const th = [0.42, 0.3, 0.5][tooth];
      x = tx + Math.random() * tw;
      y = 0.02 - Math.random() * th;
    }
    const rx = x * cos - y * sin;
    const ry = x * sin + y * cos;
    pts[i * 3] = rx * 1.35;
    pts[i * 3 + 1] = ry * 1.35 + 0.1;
    pts[i * 3 + 2] = (Math.random() - 0.5) * 0.5;
  }
  return pts;
}

function sampleArrow() {
  const pts = new Float32Array(COUNT * 3);
  const line = [
    [-2.0, -1.35], [-0.85, -0.25], [-0.15, -0.7], [1.35, 0.95],
  ];
  const segLens = [];
  let total = 0;
  for (let s = 0; s < line.length - 1; s++) {
    const dx = line[s + 1][0] - line[s][0];
    const dy = line[s + 1][1] - line[s][1];
    const l = Math.hypot(dx, dy);
    segLens.push(l);
    total += l;
  }
  for (let i = 0; i < COUNT; i++) {
    let x, y;
    if (Math.random() < 0.8) {
      let d = Math.random() * total;
      let s = 0;
      while (d > segLens[s] && s < segLens.length - 1) { d -= segLens[s]; s++; }
      const t = d / segLens[s];
      x = line[s][0] + (line[s + 1][0] - line[s][0]) * t + (Math.random() - 0.5) * 0.13;
      y = line[s][1] + (line[s + 1][1] - line[s][1]) * t + (Math.random() - 0.5) * 0.13;
    } else {
      const u = Math.random(), v = Math.random() * (1 - u);
      const ax = 1.35, ay = 0.95;
      const dirx = 0.75, diry = 0.85;
      const px = -diry, py = dirx;
      const back = 0.55;
      x = ax - dirx * back * u * 0.9 + px * (v - (1 - u) / 2) * 0.62 + dirx * 0.18;
      y = ay - diry * back * u * 0.9 + py * (v - (1 - u) / 2) * 0.62 + diry * 0.18;
    }
    pts[i * 3] = x;
    pts[i * 3 + 1] = y;
    pts[i * 3 + 2] = (Math.random() - 0.5) * 0.5;
  }
  return pts;
}

function sampleScatter() {
  const pts = new Float32Array(COUNT * 3);
  for (let i = 0; i < COUNT; i++) {
    const a = Math.random() * Math.PI * 2;
    const b = Math.acos(2 * Math.random() - 1);
    const rad = 4 + Math.random() * 6;
    pts[i * 3] = rad * Math.sin(b) * Math.cos(a);
    pts[i * 3 + 1] = rad * Math.sin(b) * Math.sin(a);
    pts[i * 3 + 2] = rad * Math.cos(b) * 0.6;
  }
  return pts;
}

const smooth = (t) => t * t * (3 - 2 * t);

export const ParticleHero = () => {
  const mountRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (window.matchMedia("(max-width: 767px)").matches) return;
    const mount = mountRef.current;
    if (!mount) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(50, mount.clientWidth / mount.clientHeight, 0.1, 100);
    camera.position.z = 6;
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    mount.appendChild(renderer.domElement);

    const keyP = sampleKey();
    const arrowP = sampleArrow();
    const burstP = sampleScatter();
    const initP = sampleScatter();

    const positions = new Float32Array(COUNT * 3);
    const sizes = new Float32Array(COUNT);
    const colors = new Float32Array(COUNT * 3);
    const phases = new Float32Array(COUNT);
    const speeds = new Float32Array(COUNT);
    const base = new THREE.Color("#FFFFFF");
    for (let i = 0; i < COUNT; i++) {
      const bright = 0.8 + Math.random() * 0.4;
      colors[i * 3] = base.r * bright;
      colors[i * 3 + 1] = base.g * bright;
      colors[i * 3 + 2] = base.b * bright;
      const z = keyP[i * 3 + 2];
      sizes[i] = 1.2 + ((z + 0.25) / 0.5) * 1.6;
      phases[i] = Math.random() * Math.PI * 2;
      speeds[i] = 0.6 + Math.random() * 1.4;
      positions[i * 3] = initP[i * 3];
      positions[i * 3 + 1] = initP[i * 3 + 1];
      positions[i * 3 + 2] = initP[i * 3 + 2];
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geo.setAttribute("aSize", new THREE.BufferAttribute(sizes, 1));
    geo.setAttribute("aColor", new THREE.BufferAttribute(colors, 3));

    const mat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: { uOpacity: { value: 0.9 } },
      vertexShader: `
        attribute float aSize;
        attribute vec3 aColor;
        varying vec3 vColor;
        void main() {
          vColor = aColor;
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = aSize * (9.0 / -mv.z);
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: `
        uniform float uOpacity;
        varying vec3 vColor;
        void main() {
          float d = length(gl_PointCoord - vec2(0.5));
          float a = smoothstep(0.5, 0.12, d);
          gl_FragColor = vec4(vColor, a * uOpacity);
        }`,
    });

    const points = new THREE.Points(geo, mat);
    scene.add(points);

    const state = { assemble: 0, scroll: 0 };
    gsap.to(state, {
      assemble: 1,
      duration: 1.4,
      ease: "power3.inOut",
      delay: 0.25,
      onComplete: () => setReady(true),
    });

    const st = ScrollTrigger.create({
      trigger: document.body,
      start: 0,
      end: () => window.innerHeight * 2.4,
      onUpdate: (self) => { state.scroll = self.progress; },
    });

    const clock = new THREE.Clock();
    let raf;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      const t = clock.getElapsedTime();
      const s = state.scroll;
      const asm = smooth(state.assemble);
      const posAttr = geo.attributes.position.array;
      for (let i = 0; i < COUNT; i++) {
        const i3 = i * 3;
        let bx, by, bz;
        if (s <= 0.35) {
          const m = smooth(s / 0.35);
          bx = keyP[i3] + (burstP[i3] - keyP[i3]) * m;
          by = keyP[i3 + 1] + (burstP[i3 + 1] - keyP[i3 + 1]) * m;
          bz = keyP[i3 + 2] + (burstP[i3 + 2] - keyP[i3 + 2]) * m;
        } else if (s <= 0.7) {
          const m = smooth((s - 0.35) / 0.35);
          bx = burstP[i3] + (arrowP[i3] - burstP[i3]) * m;
          by = burstP[i3 + 1] + (arrowP[i3 + 1] - burstP[i3 + 1]) * m;
          bz = burstP[i3 + 2] + (arrowP[i3 + 2] - burstP[i3 + 2]) * m;
        } else {
          bx = arrowP[i3]; by = arrowP[i3 + 1]; bz = arrowP[i3 + 2];
        }
        bx = initP[i3] + (bx - initP[i3]) * asm;
        by = initP[i3 + 1] + (by - initP[i3 + 1]) * asm;
        bz = initP[i3 + 2] + (bz - initP[i3 + 2]) * asm;
        const w = Math.sin(t * speeds[i] + phases[i]) * 0.028;
        posAttr[i3] = bx + w;
        posAttr[i3 + 1] = by + Math.cos(t * speeds[i] * 0.8 + phases[i]) * 0.028;
        posAttr[i3 + 2] = bz + w * 0.5;
      }
      geo.attributes.position.needsUpdate = true;
      points.rotation.y = Math.sin(t * 0.12) * 0.06;
      renderer.render(scene, camera);
    };
    tick();

    const onResize = () => {
      if (!mount.clientWidth) return;
      camera.aspect = mount.clientWidth / mount.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mount.clientWidth, mount.clientHeight);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      st.kill();
      geo.dispose();
      mat.dispose();
      renderer.dispose();
      mount.removeChild(renderer.domElement);
    };
  }, []);

  return (
    <div className="absolute inset-0 hidden md:block" data-testid="particle-hero-canvas">
      <div ref={mountRef} className="absolute inset-0" />
      {!ready && <div className="absolute inset-0 shimmer pointer-events-none" />}
    </div>
  );
};
